import { OperationVariant } from "../ai/operation.ts";

export const SCREEN_NAME_MAX_LENGTH = 100;

const DEFAULT_SCREEN_NAME = "Screen";
const DEFAULT_SCREEN_NAME_PATTERN = /^Screen(?: ([1-9]\d*))?$/;

const defaultScreenNumber = (name: string): number => {
  const match = DEFAULT_SCREEN_NAME_PATTERN.exec(name.trim());
  if (!match) return 0;
  return match[1] === undefined ? 1 : Number(match[1]);
};

export const makeDefaultScreenName = (
  existingScreenNames: readonly string[],
): string => {
  const highestExistingScreenNumber = existingScreenNames.reduce(
    (max, name) => Math.max(max, defaultScreenNumber(name)),
    0,
  );
  const screenNumber = highestExistingScreenNumber + 1;
  return screenNumber === 1
    ? DEFAULT_SCREEN_NAME
    : `${DEFAULT_SCREEN_NAME} ${screenNumber}`;
};

export const sanitizeScreenName = (screenName: string): string | undefined => {
  const sanitized = screenName
    .trim()
    .slice(0, SCREEN_NAME_MAX_LENGTH)
    .trimEnd();
  return sanitized || undefined;
};

type OperationVariantWithoutNextScreen = Exclude<
  OperationVariant,
  typeof OperationVariant.enum.next_screen
>;

// Label slots, in the order they appear in a screen name.
const LABEL_SLOTS = ["platform", "state"] as const;
type LabelSlot = (typeof LABEL_SLOTS)[number];
type VariantLabels = Partial<Record<LabelSlot, string>>;

const VARIANT_LABELS: Record<
  OperationVariantWithoutNextScreen,
  { slot: LabelSlot; text: string }
> = {
  [OperationVariant.enum.web_version]: {
    slot: "platform",
    text: "web version",
  },
  [OperationVariant.enum.mobile_version]: {
    slot: "platform",
    text: "mobile version",
  },
  [OperationVariant.enum.tablet_version]: {
    slot: "platform",
    text: "tablet version",
  },
  [OperationVariant.enum.mobile_ios_version]: {
    slot: "platform",
    text: "iOS version",
  },
  [OperationVariant.enum.mobile_android_version]: {
    slot: "platform",
    text: "Android version",
  },
  [OperationVariant.enum.error_state]: { slot: "state", text: "error state" },
  [OperationVariant.enum.empty_state]: { slot: "state", text: "empty state" },
};

const SLOT_OF_LABEL = new Map(
  Object.values(VARIANT_LABELS).map(({ slot, text }) => [text, slot]),
);

const TRUNCATION_MARK = "...";

const TRAILING_LABELS_PATTERN = /^(?<baseName>.*\S) \((?<labels>[^()]+)\)$/;

// Brackets holding anything but variant labels, e.g. a user's own "(old)", stay in the base name.
const splitVariantLabels = (
  name: string,
): { baseName: string; labels: VariantLabels } => {
  const groups = TRAILING_LABELS_PATTERN.exec(name)?.groups;
  if (!groups) {
    return { baseName: name, labels: {} };
  }

  const labels: VariantLabels = {};
  for (const label of groups.labels.split(", ")) {
    const slot = SLOT_OF_LABEL.get(label);
    if (!slot) {
      return { baseName: name, labels: {} };
    }
    labels[slot] = label;
  }
  return { baseName: groups.baseName, labels };
};

/**
 * Names hold at most one platform and one state, platform first: a new one
 * replaces the old one in its slot, so "Spring Bloom (web version, empty state)"
 * turned mobile becomes "Spring Bloom (mobile version, empty state)". A base
 * name too long to fit is shortened and ends in "..." so the brackets are
 * never cut off.
 */
const labelScreenName = (
  sourceScreenName: string,
  variant: OperationVariantWithoutNextScreen,
): string => {
  const { baseName, labels } = splitVariantLabels(sourceScreenName.trim());
  const { slot, text } = VARIANT_LABELS[variant];
  const merged: VariantLabels = { ...labels, [slot]: text };

  const ordered = LABEL_SLOTS.flatMap((labelSlot) => merged[labelSlot] ?? []);
  const suffix = ` (${ordered.join(", ")})`;

  const baseNameRoom = SCREEN_NAME_MAX_LENGTH - suffix.length;
  const cappedBaseName =
    baseName.length <= baseNameRoom
      ? baseName
      : `${baseName.slice(0, baseNameRoom - TRUNCATION_MARK.length).trimEnd()}${TRUNCATION_MARK}`;
  return `${cappedBaseName}${suffix}`;
};

export const makeExpandedScreenName = ({
  sourceScreenName,
  variant,
  newScreenName,
}: {
  sourceScreenName: string;
  variant: OperationVariant;
  newScreenName: string;
}): string | undefined =>
  variant === OperationVariant.enum.next_screen
    ? sanitizeScreenName(newScreenName)
    : labelScreenName(sourceScreenName, variant);
